import React, { useCallback } from "react"

/**
 * Returns a stable keydown handler for a role="tablist" element that moves
 * focus between role="tab" children with ArrowLeft / ArrowRight / Home / End.
 *
 * Follows the WAI-ARIA Tabs pattern — manual-activation variant: arrow keys
 * move focus only; Enter / Space on the focused tab activates it.
 * Tabs with aria-disabled="true" are skipped.
 */
export function useTabListKeyboardNavigation() {
  return useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return

    const tabs = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        '[role="tab"]:not([aria-disabled="true"])'
      )
    )
    if (tabs.length === 0) return

    const currentIndex = tabs.indexOf(document.activeElement as HTMLElement)
    if (currentIndex === -1) return

    let nextIndex: number
    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % tabs.length
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    } else if (e.key === "Home") {
      nextIndex = 0
    } else {
      nextIndex = tabs.length - 1
    }

    e.preventDefault()
    tabs[nextIndex].focus()
  }, [])
}
